﻿import { useEffect, useRef, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQueryClient } from "react-query";

import classes from "../../../purchase.module.scss";

import { ReactComponent as ActionSvg } from "@/assets/images/desktop/opciones.svg";

import { hasPermission } from "@/services/Utils";

import { key, useOrderPaginate, useOrderApprove, useOrderReject, useOrderDelete, useDownloadPdf } from "@/hooks/purchases/orders";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";

const OrdersActionItem = ({ base, item, index }) => {
	const queryClient = useQueryClient();
	const { addToast } = useToasts();

	const { filter } = useSelector((state) => state.auth);
	const { type_currency } = filter;

	const { mutate: approve, isLoading: loadApprove } = useOrderApprove(base);
	const { mutate: reject, isLoading: loadReject } = useOrderReject(base);
	const { mutate: remove, isLoading: loadDelete } = useOrderDelete(base);
	const { mutate: downloadPDF, isLoading: loadPdf } = useDownloadPdf(item.code_oc);

	const { data: response } = useOrderPaginate(base);
	const { permissions } = response;

	const navigate = useNavigate();

	const refButton = useRef(null);
	const refDiv = useRef(null);

	const [showApprove, setShowApprove] = useState(false);
	const [showReject, setShowReject] = useState(false);
	const [showDelete, setShowDelete] = useState(false);

	const handleShowAction = (show) => {
		queryClient.setQueryData([key, { ...base }], (oldData) => {
			const newData = { ...oldData };
			newData.list.data[index].show_action = show;
			return newData;
		});
	};

	const handleClickOutside = (event) => {
		if (refButton.current && !refButton.current.contains(event.target) && refDiv.current && !refDiv.current.contains(event.target)) {
			handleShowAction(false);
		}
	};

	const showAction = (index) => {
		handleShowAction(!response.list.data[index].show_action);
	};

	const handlePdf = (item) => {
		handleShowAction(false);

		downloadPDF(
			{
				code: item.code_oc,
				type_currency,
			},
			{
				onSuccess: (_response) => {
					addToast(`PDF OC ${item.code_oc} descargado correctamente.`, {
						appearance: "success",
					});
				},
			},
		);
	};

	const handleEdit = (item) => {
		handleShowAction(false);

		navigate(`/ordenes_compra/editar/${item.code}`);
	};

	const handleApprove = () => {
		setShowApprove(false);

		const request = {
			base,
			element: item,
		};

		approve(request, {
			onSuccess: (_response) => {
				addToast(`OC aprobada correctamente.`, {
					appearance: "success",
				});
			},
		});
	};

	const handleReject = () => {
		setShowReject(false);

		const request = {
			base,
			element: item,
		};

		reject(request, {
			onSuccess: (_response) => {
				addToast(`OC rechazada correctamente.`, {
					appearance: "success",
				});
			},
		});
	};

	const handleDelete = () => {
		setShowDelete(false);

		const request = {
			base,
			element: item,
		};

		remove(request, {
			onSuccess: (_response) => {
				addToast(`OC eliminada correctamente.`, {
					appearance: "success",
				});
			},
		});
	};

	useEffect(() => {
		document.addEventListener("click", handleClickOutside, true);
		// eslint-disable-next-line
	}, []);

	return (
		<>
			{loadApprove || loadReject || loadDelete || loadPdf ? (
				<Fetching topMargin={"0px"} />
			) : (
				<ActionSvg className={classes.icon} onClick={() => showAction(index)} ref={refButton} />
			)}

			{!item.show_action ? (
				""
			) : (
				<div className={`${classes.floating} ${classes.divAction}`} ref={refDiv}>
					<h4>Acciones</h4>
					<table className={classes.tableAction}>
						<tbody>
							<tr style={{ cursor: "pointer" }} onClick={() => handlePdf(item)}>
								<td className={classes.tdPDF}>
									<span className={classes.tdSpan}>Descargar</span>
								</td>
							</tr>

							{hasPermission("edit", permissions) && [1, 2, 3].includes(Number(item.state_id)) && (
								<tr style={{ cursor: "pointer" }} onClick={() => handleEdit(item)}>
									<td className={`${Number(item.state_id) === 1 ? classes.tdEdit : classes.tdShow}`}>
										<span className={classes.tdSpan}>{Number(item.state_id) === 1 ? "Editar" : "Ver"}</span>
									</td>
								</tr>
							)}
							{response && response.is_approved && Number(item.state_id) === 1 && hasPermission("approve", permissions) && (
								<tr
									style={{ cursor: "pointer" }}
									onClick={() => {
										handleShowAction(false);
										setShowApprove(true);
									}}
								>
									<td className={classes.tdApproved}>
										<span className={classes.tdSpan}>Aprobar</span>
									</td>
								</tr>
							)}
							{response && response.is_approved && Number(item.state_id) === 1 && hasPermission("approve", permissions) && (
								<tr
									style={{ cursor: "pointer" }}
									onClick={() => {
										handleShowAction(false);
										setShowReject(true);
									}}
								>
									<td className={classes.tdRejected}>
										<span className={classes.tdSpan}>Rechazar</span>
									</td>
								</tr>
							)}

							{hasPermission("delete", permissions) && (
								<tr
									style={{ cursor: "pointer" }}
									onClick={() => {
										handleShowAction(false);
										setShowDelete(true);
									}}
								>
									<td className={classes.tdDelete}>
										<span className={classes.tdSpan}>Eliminar</span>
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			)}

			<BasicConfirm
				message={"¿Está seguro de que desea aprobar la orden de compra seleccionada?"}
				showConfirm={showApprove}
				setShowConfirm={setShowApprove}
				handleAction={handleApprove}
			/>

			<BasicConfirm
				message={"¿Está seguro de que desea rechazar la orden de compra seleccionada?"}
				showConfirm={showReject}
				setShowConfirm={setShowReject}
				handleAction={handleReject}
			/>

			<BasicConfirm
				message={"¿Está seguro de que desea eliminar la orden de compra seleccionada?"}
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={handleDelete}
			/>
		</>
	);
};

export default OrdersActionItem;
